import { isAfter, isBefore, lastDayOfMonth, isPast } from 'date-fns';
import { add, sub } from 'date-fns';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import TutorialMessage from '../../components/TutorialMessage';
import { parseDate } from '../../utilities/helpers';
import { selectActiveDestinations, selectDateFilter } from '../mainFilter/selectors';

const getsavedTutorials = () => {
  const nowTimestamp = +new Date();
  const tutorials = JSON.parse(localStorage.getItem('seasonalityTutorials')) || [];

  return tutorials.filter((tutorial) => (nowTimestamp - tutorial.timestamp) / (3600000 * 24) < 1);
};

const getActualDateYear = (date) => (isPast(date) ? add(date, { years: 1 }) : date);

export default function SeasonalityTutorial() {
  const [savedTutorials, setSavedTutorials] = useState(getsavedTutorials());
  const [activeDestination] = useSelector(selectActiveDestinations);
  const dates = useSelector(selectDateFilter);

  const getActiveTutorials = () => {
    if (!activeDestination?.seasonality) return [];

    return activeDestination.seasonality.filter((range) => {
      let parsedRange = {
        dateFrom: parseDate(range.dateFrom, 'dd/MM'),
        dateTo: parseDate(range.dateTo, 'dd/MM'),
      };

      if (isBefore(parsedRange.dateTo, Date.now())) {
        parsedRange = {
          dateFrom: add(parsedRange.dateFrom, { years: 1 }),
          dateTo: add(parsedRange.dateTo, { years: 1 }),
        };
      }
      if (isBefore(parsedRange.dateTo, parsedRange.dateFrom)) {
        parsedRange = {
          ...parsedRange,
          dateFrom: sub(parsedRange.dateFrom, { years: 1 }),
        };
      }

      const parsedRangeNext = { ...parsedRange };
      parsedRangeNext.dateFrom = add(parsedRangeNext.dateFrom, { years: 1 });
      parsedRangeNext.dateTo = add(parsedRangeNext.dateTo, { years: 1 });

      if ((dates.type === 'specific' || dates.type === 'range') && dates.startDate && dates.endDate) {
        return (
          ((isAfter(parseDate(dates.startDate, 'dd/MM/yyyy'), sub(parsedRange.dateFrom, { days: 1 })) &&
            isBefore(parseDate(dates.startDate, 'dd/MM/yyyy'), add(parsedRange.dateTo, { days: 1 }))) ||
          (isAfter(parseDate(dates.endDate, 'dd/MM/yyyy'), sub(parsedRange.dateFrom, { days: 1 })) &&
            isBefore(parseDate(dates.endDate, 'dd/MM/yyyy'), add(parsedRange.dateTo, { days: 1 })))) ||
          ((isAfter(parseDate(dates.startDate, 'dd/MM/yyyy'), sub(parsedRangeNext.dateFrom, { days: 1 })) &&
            isBefore(parseDate(dates.startDate, 'dd/MM/yyyy'), add(parsedRangeNext.dateTo, { days: 1 }))) ||
          (isAfter(parseDate(dates.endDate, 'dd/MM/yyyy'), sub(parsedRangeNext.dateFrom, { days: 1 })) &&
            isBefore(parseDate(dates.endDate, 'dd/MM/yyyy'), add(parsedRangeNext.dateTo, { days: 1 }))))
        );
      }

      if (dates.type === 'month' && dates.months.length > 0) {
        const parsedMonths = dates.months.map((month) => ({
          dateFrom: getActualDateYear(parseDate(month, 'M')),
          dateTo: getActualDateYear(lastDayOfMonth(parseDate(month, 'M'))),
        }));

        return parsedMonths.some(
          (month) =>
            ((isAfter(month.dateFrom, sub(parsedRange.dateFrom, { days: 1 })) &&
              isBefore(month.dateFrom, add(parsedRange.dateTo, { days: 1 }))) ||
            (isAfter(month.dateTo, sub(parsedRange.dateFrom, { days: 1 })) &&
              isBefore(month.dateTo, add(parsedRange.dateTo, { days: 1 })))) ||
            ((isAfter(month.dateFrom, sub(parsedRangeNext.dateFrom, { days: 1 })) &&
              isBefore(month.dateFrom, add(parsedRangeNext.dateTo, { days: 1 }))) ||
            (isAfter(month.dateTo, sub(parsedRangeNext.dateFrom, { days: 1 })) &&
              isBefore(month.dateTo, add(parsedRangeNext.dateTo, { days: 1 }))))
        );
      }

      return false;
    });
  };

  const activeTutorials = getActiveTutorials();

  let uniqActiveTutorials = activeTutorials;
  if (uniqActiveTutorials.length > 1) {
    // in case of several tutorials with the same message -> There can be only one
    uniqActiveTutorials = activeTutorials.filter(
      (value, index, self) => value.message !== '' && index === self.findIndex((t) => t.message === value.message)
    );
  }

  const hideMessage = (tutorial) => {
    const currentTutorial = {
      id: activeDestination.city_google_id,
      dates: `${tutorial.dateFrom}-${tutorial.dateTo}`,
      timestamp: +new Date(),
    };

    setSavedTutorials([currentTutorial, ...savedTutorials]);

    localStorage.setItem('seasonalityTutorials', JSON.stringify([currentTutorial, ...savedTutorials]));
  };

  const isMessageVisible = (tutorial) =>
    !savedTutorials.some(
      (item) => item.id === activeDestination.city_google_id && `${tutorial.dateFrom}-${tutorial.dateTo}` === item.dates
    );

  return (
    <>
      {uniqActiveTutorials
        .slice(0, 1)
        .map(
          (tutorial) =>
            tutorial.message && (
              <TutorialMessage
                key="seasonality-tutorial"
                show={isMessageVisible(tutorial)}
                message={tutorial.message}
                onHideClick={() => hideMessage(tutorial)}
                className="mb-5"
              />
            )
        )}
    </>
  );
}
